/**
 * Opens guest project lists from the existing Product List navigation link.
 */
class GuestProjectListNavigation {
  constructor() {
    this.handleNavigationClick = this.handleNavigationClick.bind(this);
    this.handleDialogClick = this.handleDialogClick.bind(this);
    this.handleDomUpdate = this.handleDomUpdate.bind(this);
    this.editingProjects = new Set();
    this.selectedProjectId = null;
    this.labelObserver = null;
    this.initLinkLabels();
    document.addEventListener('click', this.handleNavigationClick, true);
  }

  initLinkLabels() {
    this.updateLinkLabels();

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.updateLinkLabels(), { once: true });
    }

    this.labelObserver = new MutationObserver(this.handleDomUpdate);
    this.labelObserver.observe(document.body, { childList: true, subtree: true });
  }

  handleDomUpdate() {
    this.updateLinkLabels();
  }

  updateLinkLabels() {
    document.querySelectorAll('a, button, span').forEach((element) => {
      const text = element.textContent?.replace(/\s+/g, ' ').trim();
      if (!text || !/^product lists?$/i.test(text)) return;

      element.textContent = 'Project Lists';
      if (element.hasAttribute('title')) element.setAttribute('title', 'Project Lists');
      if (element.hasAttribute('aria-label')) element.setAttribute('aria-label', 'Project Lists');
    });
  }

  handleNavigationClick(event) {
    if (document.documentElement.dataset.customer === 'true') return;

    const link = event.target.closest('a');
    if (!link || !this.isProductListLink(link)) return;

    event.preventDefault();
    event.stopPropagation();
    this.open();
  }

  isProductListLink(link) {
    return /^(product|project) lists?$/i.test(link.textContent.replace(/\s+/g, ' ').trim());
  }

  open() {
    if (!window.guestWishlistManager) return;
    this.ensureDialog();
    this.render();
    this.dialog.showModal();
  }

  ensureDialog() {
    if (this.dialog) return;

    this.dialog = document.createElement('dialog');
    this.dialog.className = 'guest-project-lists-dialog';
    this.dialog.setAttribute('aria-labelledby', 'guest-project-lists-title');
    this.dialog.innerHTML = `
      <div class="guest-project-lists-dialog__content">
        <div class="guest-project-lists-dialog__header">
          <h2 id="guest-project-lists-title">My Project Lists</h2>
          <button class="guest-project-lists-dialog__close" type="button" aria-label="Close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
          </button>
        </div>
        <div class="guest-project-lists-dialog__body"></div>
      </div>
    `;

    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });
    this.dialog.querySelector('.guest-project-lists-dialog__close').addEventListener('click', () => this.dialog.close());
    this.dialog.addEventListener('click', this.handleDialogClick);
    window.guestWishlistManager.onWishlistChange(() => this.render());
    document.body.appendChild(this.dialog);
  }

  handleDialogClick(event) {
    const selectedProject = event.target.closest('[data-guest-project-id]');
    if (selectedProject && !event.target.closest('button, a')) {
      const { guestProjectId } = selectedProject.dataset;
      if (guestProjectId && guestProjectId !== this.selectedProjectId) {
        this.selectedProjectId = guestProjectId;
        this.render();
      }
      return;
    }

    const button = event.target.closest('button[data-guest-project-action]');
    if (!button) return;

    const { guestProjectAction: action, projectId, itemId } = button.dataset;
    if (action === 'edit') {
      this.editingProjects.add(projectId);
      this.render();
      return;
    }

    if (action === 'done') {
      this.editingProjects.delete(projectId);
      this.render();
      return;
    }

    if (action === 'remove-item') {
      window.guestWishlistManager.removeItemFromWishlist(projectId, itemId);
      this.notify('Product removed from this project list.');
      return;
    }

    if (action === 'add-to-cart') this.addProjectToCart(projectId, button);
  }

  async addProjectToCart(projectId, button) {
    const project = window.guestWishlistManager.getWishlist(projectId);
    const itemQuantities = new Map();

    project?.items
      .filter((item) => item.variantId)
      .forEach((item) => {
        const variantId = String(item.variantId);
        itemQuantities.set(variantId, (itemQuantities.get(variantId) || 0) + 1);
      });

    const items = Array.from(itemQuantities, ([id, quantity]) => ({ id, quantity }));

    if (!items?.length) {
      this.notify('This project list has no products available to add to cart.', 'error');
      return;
    }

    button.disabled = true;
    button.textContent = 'Adding…';

    try {
      let addedCount = 0;

      for (const item of items) {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
          },
          body: new URLSearchParams({
            id: item.id,
            quantity: String(item.quantity)
          })
        });

        if (response.ok) {
          addedCount += item.quantity;
          continue;
        }

        const errorData = await response.json().catch(() => null);
        console.warn('Unable to add guest project item to cart', item, errorData);
      }

      if (!addedCount) throw new Error('Unable to add any project list items to cart');
      window.location.assign('/cart');
    } catch (error) {
      button.disabled = false;
      button.textContent = 'Add All to Cart';
      this.notify('Some products could not be added to cart. Please try again.', 'error');
    }
  }

  notify(message, type = 'success') {
    window.dispatchEvent(new CustomEvent('wishlistNotification', { detail: { message, type } }));
  }

  render() {
    if (!this.dialog || !window.guestWishlistManager) return;

    const body = this.dialog.querySelector('.guest-project-lists-dialog__body');
    const projects = window.guestWishlistManager.getAllWishlists();
    body.replaceChildren();

    if (!projects.some((project) => project.id === this.selectedProjectId)) {
      this.selectedProjectId = projects[0]?.id || null;
    }

    if (!projects.length) {
      const empty = document.createElement('div');
      empty.className = 'guest-project-lists-dialog__empty';
      empty.innerHTML = '<h3>No Project Lists Yet</h3><p>Add a product to a project list to start building your first project.</p>';
      body.appendChild(empty);
      return;
    }

    const list = document.createElement('div');
    list.className = 'guest-project-lists-dialog__list';
    projects.forEach((project) => list.appendChild(this.createProject(project)));
    body.appendChild(list);
  }

  createProject(project) {
    const section = document.createElement('section');
    section.className = 'guest-project-lists-dialog__project';
    section.dataset.guestProjectId = project.id;
    if (project.id === this.selectedProjectId) section.classList.add('is-selected');

    const heading = document.createElement('div');
    heading.className = 'guest-project-lists-dialog__project-heading';
    const projectDetails = document.createElement('div');
    projectDetails.className = 'guest-project-lists-dialog__project-details';
    const title = document.createElement('h3');
    title.textContent = project.name;
    const count = document.createElement('p');
    count.textContent = `${project.items.length} item${project.items.length === 1 ? '' : 's'}`;
    projectDetails.append(title, count);

    const actions = document.createElement('div');
    actions.className = 'guest-project-lists-dialog__project-actions';
    const isEditing = this.editingProjects.has(project.id);
    const editButton = this.createActionButton(isEditing ? 'Done' : 'Edit List', isEditing ? 'done' : 'edit', project.id, 'secondary');
    actions.appendChild(editButton);
    if (project.items.length) actions.appendChild(this.createActionButton('Add All to Cart', 'add-to-cart', project.id, 'primary'));
    heading.append(projectDetails, actions);
    section.appendChild(heading);

    if (!project.items.length) {
      const empty = document.createElement('p');
      empty.className = 'guest-project-lists-dialog__project-empty';
      empty.textContent = 'No products in this project list.';
      section.appendChild(empty);
      return section;
    }

    const items = document.createElement('div');
    items.className = 'guest-project-lists-dialog__items';
    project.items.forEach((item) => {
      const card = document.createElement('article');
      card.className = 'guest-project-lists-dialog__item';
      const link = document.createElement('a');
      link.className = 'guest-project-lists-dialog__item-link';
      link.href = `/products/${item.handle}`;
      const image = document.createElement('img');
      image.src = item.image;
      image.alt = '';
      image.loading = 'lazy';
      const name = document.createElement('span');
      name.textContent = item.title;
      link.append(image, name);
      card.appendChild(link);

      if (this.editingProjects.has(project.id)) {
        const removeButton = this.createActionButton('Remove', 'remove-item', project.id, 'remove');
        removeButton.dataset.itemId = item.id;
        card.appendChild(removeButton);
      }
      items.appendChild(card);
    });
    section.appendChild(items);
    return section;
  }

  createActionButton(label, action, projectId, style) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `guest-project-lists-dialog__action guest-project-lists-dialog__action--${style}`;
    button.dataset.guestProjectAction = action;
    button.dataset.projectId = projectId;
    button.textContent = label;
    return button;
  }
}

window.guestProjectListNavigation = new GuestProjectListNavigation();
